import {
  Badge,
  Button,
  Card,
  Divider,
  Flex,
  List,
  Typography,
  theme,
} from "antd";
import { ReactNode } from "react";

const { Title, Text } = Typography;

export interface PlanFeature {
  feature: string;
  included: boolean;
}

interface PlanCardProps {
  title: string;
  subtitle: string;
  price: string;
  buttonText: string;
  features: PlanFeature[];
  icon?: ReactNode;
  renderFeatureIcon: (included: boolean) => ReactNode;
  buttonStyles?: React.CSSProperties;
  onButtonClick?: () => void;
  isButtonLoading?: boolean;
  isSelected?: boolean;
  ribbonColor?: string;
  ribbonText?: string;
}

const PlanCard: React.FC<PlanCardProps> = ({
  title,
  subtitle,
  price,
  buttonText,
  features,
  icon,
  renderFeatureIcon,
  buttonStyles = {},
  isButtonLoading = false,
  onButtonClick,
  isSelected,
  ribbonColor,
  ribbonText,
}) => {
  const { token } = theme.useToken();

  const render = () => {
    return (
      <Card
        style={{
          width: 350,
          height: 550,
          borderRadius: token.borderRadiusLG,
          ...(isSelected
            ? {
                borderColor: token.colorPrimary,
                borderWidth: 2,
                boxShadow: `0 4px 20px rgba(0, 0, 0, 0.1)`,
              }
            : {
                boxShadow: token.boxShadow,
              }),
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Flex vertical gap={token.marginMD} style={{ height: "100%" }}>
          <Typography>
            <Flex align="center" gap={token.marginXS}>
              {icon}
              <Title level={2} style={{ marginBottom: 0 }}>
                {title}
              </Title>
            </Flex>
            <Text type="secondary">{subtitle}</Text>
          </Typography>

          <Flex align="baseline" gap={token.marginXS}>
            <Title level={1} style={{ margin: 0 }}>
              {price}
            </Title>
            <Text type="secondary">per month</Text>
          </Flex>

          <Button
            type="primary"
            size="large"
            loading={isButtonLoading}
            block
            disabled={isButtonLoading || !onButtonClick}
            style={{
              marginTop: token.marginSM,
              marginBottom: token.marginMD,
              ...buttonStyles,
            }}
            onClick={onButtonClick}
          >
            {buttonText}
          </Button>

          <Divider style={{ margin: `${token.marginSM}px 0` }} />

          <List
            itemLayout="horizontal"
            dataSource={features}
            renderItem={(item) => (
              <List.Item style={{ padding: `${token.paddingXS}px 0` }}>
                <Flex align="center" gap={token.marginSM}>
                  {renderFeatureIcon(item.included)}
                  <Text>{item.feature}</Text>
                </Flex>
              </List.Item>
            )}
            style={{ flex: 1 }}
          />
        </Flex>
      </Card>
    );
  };

  if (ribbonText) {
    return (
      <Badge.Ribbon
        text={ribbonText}
        color={ribbonColor ? ribbonColor : token.colorPrimary}
      >
        {render()}
      </Badge.Ribbon>
    );
  }

  return render();
};

export default PlanCard;
